import React, { useState, useMemo } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { editApplicationEconomicFrameworkValueWp } from '@lib/api';
import { InputNumber } from 'primereact';
import { formatToDecimals } from 'lib/helpers/formatting';
import { OETrans } from 'components/translation/OETrans';
import StatusBar from 'components/StatusBar';

const CompanyCardsComponent = ({
	companyCode,
	economicsFramValues,
	originEconomicsFramValues,
	myUserCompanies,
	isCoordinator,
	isAboveGuest,
	isEnabledToView,
	itemNames,
	localLang,
	toggleState,
	toggle,
	setEconomicsFramValues,
	saveEconomicsFramework,
	totalByFieldName,
}) => {
	const [sbShow, setSbShow] = useState(<></>);
	const thisElement = useMemo(() => economicsFramValues[companyCode], [economicsFramValues, companyCode]);
	const isBeneficiary = myUserCompanies.some((companyId) => companyId === thisElement.companyId);
	const [isModified, setIsModified] = useState(false);

	const handleStatusBar = (status, message, timeout, customStyle) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} customStyle={customStyle} />);
	};

	const onChange = (e, type, companyCode, id, fieldName) => {
		let value = e.value ?? 0;
		let tempEconomicsFramValues = { ...economicsFramValues };

		setIsModified(true);

		switch (type) {
			case 'amount':
				tempEconomicsFramValues[companyCode].items.find((item) => item.id === id).amount = value;

				const companyEl = tempEconomicsFramValues[companyCode];

				let total = companyEl.items.reduce((total, item) => {
					return total + item.amount;
				}, 0);

				tempEconomicsFramValues[companyCode].wpFramework.totalEligibleCosts = total;

				setEconomicsFramValues(tempEconomicsFramValues);
				break;
			case 'other':
				if (fieldName === 'indirectCosts') {
					tempEconomicsFramValues[companyCode].wpFramework.totalEligibleCosts += value;
				}

				tempEconomicsFramValues[companyCode].wpFramework[fieldName] = value;

				totalByFieldName(fieldName, companyCode);
				break;
			default:
				break;
		}
	};

	let elements = (
		<Col key={companyCode} xl={6} className="mt-0">
			<Card className="card-econframe-companies h-95">
				<Card.Body className="d-flex flex-column">
					<Card.Title className="f-700">{thisElement.companyName}</Card.Title>

					<Card.Text as="div">
						<Row className="headers">
							<Col>Voci</Col>
							<Col className="text-end">Importo</Col>
						</Row>

						{thisElement.items.map((item, index) => {
							return (
								<Row key={item.id} className="expense-items">
									<Col className="align-self-center">{item.projectNoticeExpenseItem.name}</Col>
									<Col className="col-5 col-sm-4 text-end">
										{!toggleState[companyCode] ? (
											<span>{formatToDecimals(item.amount ?? 0)}</span>
										) : (
											<InputNumber
												id={item.id}
												key={item.id}
												name="amount"
												min={0}
												step={1}
												minFractionDigits={2}
												maxFractionDigits={2}
												onChange={(e) => onChange(e, 'amount', companyCode, item.id, undefined)}
												mode="decimal"
												locale={localLang}
												className="ml input-candidacy-wp justify-content-end"
												value={item.amount ?? 0}
												disabled={!(isEnabledToView || ((isCoordinator || isBeneficiary) && isAboveGuest))}
											/>
										)}
									</Col>
								</Row>
							);
						})}

						{thisElement.wpFramework
							? Object.keys(itemNames)
									.sort((a, b) => a.position - b.position)
									.map((key, i) => {
										const { label, isAtotal } = itemNames[key];

										return (
											<Row key={key} className="expense-items">
												<Col className="align-self-center">
													<span>{label}</span>
												</Col>
												<Col className="col-5 col-sm-4 text-end">
													{isAtotal || !toggleState[companyCode] ? (
														<span>{formatToDecimals(thisElement.wpFramework[key] ?? 0)}</span>
													) : (
														<InputNumber
															id={key}
															key={key}
															name={key}
															min={0}
															step={1}
															minFractionDigits={2}
															maxFractionDigits={2}
															onChange={(e) => onChange(e, 'other', companyCode, undefined, key)}
															mode="decimal"
															locale={localLang}
															className="ml input-candidacy-wp justify-content-end"
															value={thisElement.wpFramework[key] ?? 0}
															disabled={!(isEnabledToView ||  ((isCoordinator || isBeneficiary) && isAboveGuest))}
														/>
													)}
												</Col>
											</Row>
										);
									})
							: null}

						{(isEnabledToView || ((isCoordinator || isBeneficiary) && isAboveGuest)) && (
							<>
								<Row className="justify-content-end px-4 pt-2 pb-3">
									<Col className="p-0">{sbShow}</Col>

									<Col className="col-auto p-0 text-end">
										{!toggleState[companyCode] ? (
											<Button className="btn btn-primary ms-3" onClick={() => toggle(companyCode)}>
												<OETrans code="common.edit" fallback={'modifica'} />
											</Button>
										) : (
											<>
												<Button
													variant="outline-cancel"
													className="btn"
													onClick={() => {
														toggle(companyCode);
														setEconomicsFramValues((prev) => ({
															...prev,
															[companyCode]: structuredClone(originEconomicsFramValues[companyCode]),
														}));
													}}
												>
													<OETrans code="common.cancel" fallback={'Annulla'} />
												</Button>
												<Button
													className="btn btn-primary ms-3"
													onClick={() => saveEconomicsFramework(companyCode, handleStatusBar, setSbShow, setIsModified)}
													disabled={!isModified}
												>
													<OETrans code="common.save" fallback={'Salva'} />
												</Button>
											</>
										)}
									</Col>
								</Row>
							</>
						)}
					</Card.Text>
				</Card.Body>
			</Card>
		</Col>
	);

	return <>{elements}</>;
};

const EconomicFrameworkCompanies = ({
	projectId,
	itemNames,
	isCoordinator,
	isAboveGuest,
	isEnabledToView,
	myUserCompanies,
	localLang,
	economicsFramValues1,
	setIsRefresh,
}) => {
	const [originEconomicsFramValues, setOriginEconomicsFramValues] = useState(() => structuredClone(economicsFramValues1));
	const [economicsFramValues, setEconomicsFramValues] = useState(() => structuredClone(economicsFramValues1));
	const [toggleState, setToggleState] = useState({});

	const totalByFieldName = (fieldName, companyCode) => {
		let tempEconomicsFramValues = { ...economicsFramValues };
		let companyEl = tempEconomicsFramValues[companyCode];

		//totalEligibleCosts * fundingRate (%)
		companyEl.wpFramework.maximumEuContribution = (companyEl.wpFramework.totalEligibleCosts * companyEl.wpFramework.fundingRate) / 100;

		//requestedEuContribution
		companyEl.wpFramework.maxGrantAmount = companyEl.wpFramework.requestedEuContribution;

		//maxGrantAmount + incomeGenerated + financialContribution + otherFundingSources + ownResources
		companyEl.wpFramework.totalEstimatedIncome =
			companyEl.wpFramework.maxGrantAmount +
			companyEl.wpFramework.incomeGenerated +
			companyEl.wpFramework.financialContribution +
			companyEl.wpFramework.otherFundingSources +
			companyEl.wpFramework.ownResources;

		setEconomicsFramValues(tempEconomicsFramValues);
	};

	const toggle = (companyCode) => {
		setToggleState((prev) => ({
			...prev,
			[companyCode]: !prev[companyCode],
		}));
	};

	const saveEconomicsFramework = async (activeCompanyCode, handleStatusBar, setSbShow, setIsModified) => {
		setSbShow(<></>);

		const wpFrameworkId = economicsFramValues[activeCompanyCode].wpFramework.id;
		const result = await editApplicationEconomicFrameworkValueWp(projectId, wpFrameworkId, economicsFramValues[activeCompanyCode]);

		if (result.success) {
			handleStatusBar('success', `Informazioni salvate con successo`, true, { margin: 0 });

			setIsRefresh(true);

			setOriginEconomicsFramValues((prev) => ({
				...prev,
				[activeCompanyCode]: structuredClone(economicsFramValues[activeCompanyCode]),
			}));

			toggle(activeCompanyCode);
			setIsModified(false);
		} else {
			handleStatusBar('error', `Salvataggio fallito`, false, { margin: 0 });
		}
	};

	const companyCardsSection = () => {
		const companyCodes = Object.keys(economicsFramValues);

		let rows = companyCodes.map((companyCode, index) => {
			return (
				<CompanyCardsComponent
					key={companyCode}
					companyCode={companyCode}
					economicsFramValues={economicsFramValues}
					originEconomicsFramValues={originEconomicsFramValues}
					myUserCompanies={myUserCompanies}
					isCoordinator={isCoordinator}
					isAboveGuest={isAboveGuest}
					isEnabledToView={isEnabledToView}
					itemNames={itemNames}
					localLang={localLang}
					toggleState={toggleState}
					toggle={toggle}
					setEconomicsFramValues={setEconomicsFramValues}
					saveEconomicsFramework={saveEconomicsFramework}
					totalByFieldName={totalByFieldName}
				/>
			);
		});

		return (
			<>
				<Row className="gx-5 align-items-stretch">{rows}</Row>
			</>
		);
	};

	return (
		<>
			<Row className="row d-flex align-items-center mt-3 mb-4">
				<Col className="col-4">
					<h5 className="title-box">
						<OETrans
							code={isEnabledToView || isCoordinator ? 'common.economicframeworks.companies' : 'common.economicframeworks.company'}
							fallback={'Società'}
						/>
					</h5>
				</Col>
			</Row>

			{companyCardsSection()}
		</>
	);
};
export default EconomicFrameworkCompanies;
